import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { qdfAPI } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import { ZONE_OPTIONS } from './QDFConstants';
import AppShell from '../../components/layout/AppShell';
import {
  Button,
  PageHeader,
  Alert,
  Card,
  CardHeader,
  CardBody,
  Input,
  Select,
  Modal,
  DataTable,
  ConfirmDialog,
} from '../../components/ui';
import Pagination from './components/Pagination';

const INITIAL_FORM = {
  shopLabel: '',
  assetTag: '',
  assetNumber: '',
  location: '',
  zone: '',
  department: '',
};

const SHOPS_PER_PAGE = 20;

function QDFSettingsPage() {
  const { role } = useContext(AuthContext);
  const [shops, setShops] = useState([]);
  const [pendingDeleteShop, setPendingDeleteShop] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const availableGroups = useMemo(() => {
    return Array.from(new Set(shops.map((shop) => shop.shopLabel).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [shops]);

  const availableLocations = useMemo(() => {
    return Array.from(new Set(shops.map((shop) => shop.location).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [shops]);

  const availableDepartments = useMemo(() => {
    return Array.from(new Set(shops.map((shop) => shop.department).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [shops]);

  const availableZones = useMemo(() => {
    const knownZones = Object.values(ZONE_OPTIONS).flat();
    const savedZones = shops.map((shop) => shop.zone).filter(Boolean);
    return Array.from(new Set([...knownZones, ...savedZones])).sort((a, b) => a.localeCompare(b));
  }, [shops]);

  const availableFormZones = useMemo(() => {
    if (!formData.location) {
      return availableZones;
    }

    const locationZones = ZONE_OPTIONS[formData.location] || [];
    return Array.from(new Set([...locationZones, ...shops
      .filter((shop) => shop.location === formData.location)
      .map((shop) => shop.zone)
      .filter(Boolean)])).sort((a, b) => a.localeCompare(b));
  }, [availableZones, formData.location, shops]);

  const filteredShops = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return shops.filter((shop) => {
      const matchesKeyword = !keyword || [
        shop.shopLabel,
        shop.assetTag,
        shop.assetNumber,
        shop.location,
        shop.zone,
        shop.department,
      ]
        .join(' ')
        .toLowerCase()
        .includes(keyword);

      const matchesGroup = !groupFilter || shop.shopLabel === groupFilter;
      const matchesLocation = !locationFilter || shop.location === locationFilter;
      const matchesZone = !zoneFilter || shop.zone === zoneFilter;
      const matchesDepartment = !departmentFilter || shop.department === departmentFilter;

      return matchesKeyword && matchesGroup && matchesLocation && matchesZone && matchesDepartment;
    });
  }, [query, shops, groupFilter, locationFilter, zoneFilter, departmentFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredShops.length / SHOPS_PER_PAGE)),
    [filteredShops.length]
  );

  const paginatedShops = useMemo(() => {
    const startIndex = (currentPage - 1) * SHOPS_PER_PAGE;
    return filteredShops.slice(startIndex, startIndex + SHOPS_PER_PAGE);
  }, [currentPage, filteredShops]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const loadShops = useCallback(async () => {
    setLoading(true);
    try {
      const response = await qdfAPI.getShops();
      const serverShops = response?.data?.shops || [];

      if (serverShops.length === 0 && role === 'admin') {
        await qdfAPI.bootstrapShops();
        const seededResponse = await qdfAPI.getShops();
        setShops(seededResponse?.data?.shops || []);
      } else {
        setShops(serverShops);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load QDF shops.');
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === 'location') {
        const nextZones = ZONE_OPTIONS[value] || [];
        const keepZone = prev.zone && nextZones.includes(prev.zone);
        return { ...prev, location: value, zone: keepZone ? prev.zone : '' };
      }

      return { ...prev, [name]: value };
    });
  };

  const handleEdit = (shop) => {
    setEditingId(shop.id);
    setFormData({
      shopLabel: shop.shopLabel || '',
      assetTag: shop.assetTag || '',
      assetNumber: shop.assetNumber || '',
      location: shop.location || '',
      zone: shop.zone || '',
      department: shop.department || '',
    });
  };

  const resetForm = () => {
    setEditingId('');
    setFormData(INITIAL_FORM);
  };

  const closeEditModal = () => {
    resetForm();
  };

  const validateForm = () => {
    const requiredFields = ['shopLabel', 'assetTag', 'location', 'department'];
    const missing = requiredFields.filter((field) => !formData[field]?.trim());

    if (missing.length > 0) {
      toast.error(`Please fill required fields: ${missing.join(', ')}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingId) {
        await qdfAPI.updateShop(editingId, formData);
        toast.success('Shop updated successfully.');
      } else {
        await qdfAPI.createShop(formData);
        toast.success('Shop created successfully.');
      }

      resetForm();
      await loadShops();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to save shop.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (shop) => setPendingDeleteShop(shop);

  const confirmDeleteShop = async () => {
    if (!pendingDeleteShop) return;

    setDeleting(true);
    try {
      await qdfAPI.deleteShop(pendingDeleteShop.id);
      toast.success('Shop deleted.');
      await loadShops();
      if (editingId === pendingDeleteShop.id) {
        resetForm();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to delete shop.');
    } finally {
      setDeleting(false);
      setPendingDeleteShop(null);
    }
  };

  if (role !== 'admin') {
    return (
      <AppShell>
        <Alert variant="error" title="Access denied">
          You are not allowed to access QDF settings.
        </Alert>
      </AppShell>
    );
  }

  const hasFilters = Boolean(query || groupFilter || locationFilter || zoneFilter || departmentFilter);

  const clearFilters = () => {
    setQuery('');
    setGroupFilter('');
    setLocationFilter('');
    setZoneFilter('');
    setDepartmentFilter('');
    setCurrentPage(1);
  };

  const shopColumns = [
    { key: 'assetTag', header: 'Asset tag', primary: true, className: 'tabular' },
    { key: 'shopLabel', header: 'Group' },
    { key: 'assetNumber', header: 'Asset number', className: 'tabular' },
    { key: 'location', header: 'Location' },
    { key: 'zone', header: 'Zone' },
    { key: 'department', header: 'Department' },
    {
      key: 'actions',
      header: 'Actions',
      render: (shop) => (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" icon={EditOutlined} onClick={() => handleEdit(shop)}>
            Edit
          </Button>
          <Button
            variant="danger-soft"
            size="sm"
            icon={DeleteOutline}
            onClick={() => handleDelete(shop)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const renderShopForm = ({ isEditing = false } = {}) => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Select
        label="Group label"
        name="shopLabel"
        value={formData.shopLabel}
        onChange={handleFormChange}
      >
        <option value="">Select group label</option>
        {availableGroups.map((group) => (
          <option key={group} value={group}>{group}</option>
        ))}
      </Select>

      <Input
        label="Asset tag"
        name="assetTag"
        value={formData.assetTag}
        onChange={handleFormChange}
        placeholder="e.g. BGM-001"
      />

      <Select
        label="Location"
        name="location"
        value={formData.location}
        onChange={handleFormChange}
      >
        <option value="">Select location</option>
        {availableLocations.map((location) => (
          <option key={location} value={location}>{location}</option>
        ))}
      </Select>

      <Select
        label="Zone"
        name="zone"
        value={formData.zone}
        onChange={handleFormChange}
        hint="Optional. Assign the individual shop to a specific zone."
      >
        <option value="">Select zone</option>
        {availableFormZones.map((zone) => (
          <option key={zone} value={zone}>{zone}</option>
        ))}
      </Select>

      <Select
        label="Department"
        name="department"
        value={formData.department}
        onChange={handleFormChange}
      >
        <option value="">Select department</option>
        {availableDepartments.map((department) => (
          <option key={department} value={department}>{department}</option>
        ))}
      </Select>

      <div className="flex gap-2 pt-1">
        <Button type="submit" loading={saving} fullWidth={!isEditing}>
          {isEditing ? 'Update shop' : 'Create shop'}
        </Button>
        {isEditing && (
          <Button type="button" variant="secondary" onClick={closeEditModal}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );

  return (
    <>
      <PageHeader
        title="QDF shop settings"
        subtitle="Shop mappings used by the QDF forms"
        backTo="/qdf-main"
      />

      <AppShell>
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <Card className="h-fit xl:col-span-1">
            <CardHeader title="Add shop" />
            <CardBody>
              {renderShopForm()}
            </CardBody>
          </Card>

          <Card className="xl:col-span-2">
            <CardHeader
              title="Current shops"
              subtitle={`${shops.length} configured`}
              actions={
                hasFilters ? (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                ) : null
              }
            />
            <CardBody>
              <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <Input
                  label="Search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Tag, number, location…"
                />
                <Select label="Group" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
                  <option value="">All groups</option>
                  {availableGroups.map((group) => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </Select>
                <Select
                  label="Location"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="">All locations</option>
                  {availableLocations.map((location) => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </Select>
                <Select
                  label="Zone"
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                >
                  <option value="">All zones</option>
                  {availableZones.map((zone) => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </Select>
                <Select
                  label="Department"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="">All departments</option>
                  {availableDepartments.map((department) => (
                    <option key={department} value={department}>{department}</option>
                  ))}
                </Select>
              </div>

              <DataTable
                columns={shopColumns}
                rows={paginatedShops}
                keyField="id"
                loading={loading}
                emptyTitle="No shops found"
                emptyDescription={
                  hasFilters ? 'No shops match the current filters.' : 'Add a shop to get started.'
                }
                emptyAction={
                  hasFilters ? (
                    <Button variant="secondary" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  ) : null
                }
              />

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredShops.length}
                itemsPerPage={SHOPS_PER_PAGE}
                onPageChange={setCurrentPage}
              />
            </CardBody>
          </Card>
        </div>
      </AppShell>

      <ConfirmDialog
        isOpen={Boolean(pendingDeleteShop)}
        onClose={() => setPendingDeleteShop(null)}
        onConfirm={confirmDeleteShop}
        loading={deleting}
        title="Delete shop"
        message={`This removes the mapping for ${pendingDeleteShop?.assetTag}. Existing work orders keep their recorded values.`}
        confirmLabel="Delete"
      />

      <Modal
        isOpen={Boolean(editingId)}
        onClose={closeEditModal}
        size="md"
        title="Edit shop"
        subtitle={formData.assetTag || 'Update shop mapping'}
        footer={null}
      >
        {renderShopForm({ isEditing: true })}
      </Modal>
    </>
  );
}

export default QDFSettingsPage;
